import React, { useState, useEffect, useRef } from 'react';
import SelectSymbol from '../../../components/SelectSymbol/SelectSymbol';
import ActionMessage from '../../../components/ActionMessage/ActionMessage';
import { applyStrategyTemplate, applyParametricTemplate, applyAutoTemplate } from '../../../services/StrategiesService';
import { getSettings, getMarketLiquidity } from '../../../services/SettingsService';
import { getTestnetAvailability } from '../../../services/SymbolsService';
import { processError } from '../../../services/ServiceUtils';
import { DEFAULT_SYMBOL } from '../../../utils/SymbolUtils';
import { getCurrentEnvironment } from '../../../utils/CurrentEnvironmentStorage';
import { confirmDialog } from '../../../utils/ConfirmDialog';

// Templates simples que ganham modo 'Vários ativos' (list): 1 estratégia → N ativos concretos.
const SIMPLE_MULTI_IDS = ['AI_TRADER', 'EMA_CROSSOVER', 'DCA_DIP', 'REGIME_DCA'];

// Templates que suportam alvo multi (mostram o toggle Um ativo / Vários [/ Automático]).
// AI Full = coringa por scanner (long); Short Futuros = concreto nas moedas em queda; os simples = list.
function supportsParametric(template) {
    return template && (template.id === 'AI_FULL' || template.id === 'FUTURES_SHORT' || SIMPLE_MULTI_IDS.includes(template.id));
}

// Modo 'Automático' (scanner/coringa): o scanner escolhe e rotaciona os ativos. AI Full, Short
// Futuros, AI Trader (Fase A), EMA/DCA (Fase B) e DCA-Regime (item 8 — compra agendada com fan-out
// coringa sobre o foco). O orquestrador provisiona os indicadores/regime por símbolo de foco.
function supportsAuto(template) {
    return template && ['AI_FULL', 'FUTURES_SHORT', 'AI_TRADER', 'EMA_CROSSOVER', 'DCA_DIP', 'REGIME_DCA'].includes(template.id);
}

// Presets de parâmetros (Conservador/Moderado/Agressivo) — pré-preenchem os campos do template.
const PRESET_META = {
    CONSERVATIVE: { label: '🛡️ Conservador', cls: 'success' },
    MODERATE:     { label: '⚖️ Moderado',    cls: 'primary' },
    AGGRESSIVE:   { label: '🚀 Agressivo',   cls: 'danger'  },
};
const PRESET_ORDER = ['CONSERVATIVE', 'MODERATE', 'AGGRESSIVE'];

// Detecta qual preset bate com a config atual (ou null = personalizado). Compara só as chaves
// que o preset define; números com tolerância (campos vêm como string do input).
function detectPreset(presets, config) {
    if (!presets) return null;
    for (const name of PRESET_ORDER) {
        const p = presets[name];
        if (!p) continue;
        const ok = Object.keys(p).every(k => {
            const a = config[k], b = p[k];
            if (typeof b === 'number') return Math.abs((parseFloat(a) || 0) - b) < 1e-9;
            return String(a ?? '') === String(b);
        });
        if (ok) return name;
    }
    return null;
}

// Extrai a lista de bases do texto livre: aceita vírgula/espaço, normaliza maiúsculas,
// remove o sufixo do quote se o usuário digitar o par inteiro (ex: "BTCUSDT" -> "BTC").
function parseBases(text, quote) {
    return [...new Set(
        (text || '')
            .split(/[\s,]+/)
            .map(b => b.trim().toUpperCase())
            .filter(Boolean)
            .map(b => (quote && b.endsWith(quote) && b.length > quote.length) ? b.slice(0, -quote.length) : b)
    )];
}

const DIFFICULTY_BADGE = {
    'Iniciante':     'bg-success',
    'Intermediário': 'bg-warning text-dark',
    'Avançado':      'bg-danger',
};

/**
 * props:
 * - template  (template object or null)
 * - onApplied(strategy) — called after successful creation
 */
function TemplateConfigModal({ template, onApplied, initialTargetMode }) {
    const btnClose = useRef('');
    const [symbol, setSymbol] = useState(DEFAULT_SYMBOL);
    const [config, setConfig] = useState({});
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [targetMode, setTargetMode] = useState('single'); // 'single' | 'list'
    const [basesText, setBasesText] = useState('');
    const [preferredQuote, setPreferredQuote] = useState('USDT');
    // Modo do formulário: 'simple' (só o nível + essenciais) ou 'advanced' (todos os params). Os campos
    // que o preset CONTROLA somem no Simples (o nível já os define); só de apresentação → localStorage.
    const [formMode, setFormMode] = useState(() => localStorage.getItem('templateConfigMode') || 'simple');
    const changeFormMode = m => { setFormMode(m); try { localStorage.setItem('templateConfigMode', m); } catch (_) {} };

    const environment = getCurrentEnvironment();
    const isTest = environment === 'test';
    const canParametric = supportsParametric(template);
    const parsedBases = parseBases(basesText, preferredQuote);
    const [lowLiq, setLowLiq] = useState([]); // pares com volume 24h baixo (aviso, não bloqueia)
    // Disponibilidade na TESTNET: Set de símbolos que existem lá, ou null quando não há
    // informação (produção — nunca busca; ou fail-open do backend). null = nenhum aviso.
    const [testnetAvailable, setTestnetAvailable] = useState(null);

    const pairs = targetMode === 'list'
        ? parsedBases.map(b => `${b}${preferredQuote}`)
        : (symbol ? [symbol] : []);

    // Aviso de baixa liquidez: consulta o volume 24h dos pares escolhidos (manuais). O modo automático
    // já pondera liquidez no scanner; aqui cobre a escolha na mão (single/lista). Advisory — não bloqueia.
    useEffect(() => {
        if (!pairs.length) { setLowLiq([]); return; }
        let alive = true;
        getMarketLiquidity(pairs)
            .then(r => { if (alive && r && r.symbols) setLowLiq(pairs.filter(p => r.symbols[p] && r.symbols[p].low === true)); })
            .catch(() => { /* silencioso: aviso é best-effort */ });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [symbol, basesText, targetMode, preferredQuote]);

    // Disponibilidade na testnet (dropdown ciente da testnet, 14/07): só busca em ambiente
    // 'test' — em produção NENHUMA chamada nova é feita (gate antes do fetch; o sync já
    // reflete a realidade lá). Fail-open: erro/`available: null` do backend → sem aviso.
    useEffect(() => {
        if (!isTest) return;
        getTestnetAvailability()
            .then(r => { if (r && Array.isArray(r.available)) setTestnetAvailable(new Set(r.available)); })
            .catch(() => { /* fail-open: mantém null */ });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Pares escolhidos que NÃO existem na testnet — só calculado com env=test E informação
    // disponível. Substitui o aviso de liquidez (que nesse caso é ruído: o problema real é a
    // estratégia ficar muda, não o slippage).
    const testnetUnavailable = (isTest && testnetAvailable) ? pairs.filter(p => !testnetAvailable.has(p)) : [];

    useEffect(() => {
        if (!template) return;
        const defaults = {};
        (template.configFields || []).forEach(f => { defaults[f.key] = f.default; });
        setConfig(defaults);
        setError('');
        // Alvo inicial: 'single' por padrão; quando aberto pela ponte do Nova Estratégia
        // (vários ativos/automático), já chega pré-selecionado pra não re-escolher.
        setTargetMode((canParametric && initialTargetMode) ? initialTargetMode : 'single');
        setBasesText('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [template, initialTargetMode]);

    // Carrega o quote preferencial do usuário pra montar os pares (base + quote).
    useEffect(() => {
        if (!supportsParametric(template)) return;
        getSettings()
            .then(s => { if (s && s.preferredQuote) setPreferredQuote(s.preferredQuote); })
            .catch(() => { /* mantém USDT como default */ });
    }, [template]);

    function onFieldChange(key, value) {
        setConfig(prev => ({ ...prev, [key]: value }));
    }

    function applyPreset(name) {
        const p = template && template.presets && template.presets[name];
        if (!p) return;
        setConfig(prev => ({ ...prev, ...p }));
    }

    const isFutures = template && template.id === 'FUTURES_SHORT';
    const activePreset = detectPreset(template && template.presets, config);
    // Campos só-do-automático (ex: nº de moedas do short) ficam ocultos fora do modo auto.
    const visibleFields = (template?.configFields || []).filter(f => !f.autoOnly || targetMode === 'auto');
    // Chaves que os presets de nível controlam → no modo Simples ficam ocultas (o nível já as define);
    // só os campos NÃO cobertos por preset (essenciais manuais) aparecem no Simples.
    const presetCoveredKeys = new Set();
    if (template && template.presets)
        Object.values(template.presets).forEach(p => Object.keys(p || {}).forEach(k => presetCoveredKeys.add(k)));
    const hasAdvancedFields = visibleFields.some(f => presetCoveredKeys.has(f.key));
    const fieldsToShow = (formMode === 'advanced' || !template?.presets)
        ? visibleFields
        : visibleFields.filter(f => !presetCoveredKeys.has(f.key));

    function onSymbolChange(e) {
        setSymbol(e.target.value);
    }

    async function onApply(replace = false) {
        if (!template) return;
        if (targetMode === 'list' && parsedBases.length === 0) {
            setError('Informe ao menos um ativo base (ex: BTC, ETH, SOL).');
            return;
        }
        setLoading(true);
        setError('');
        try {
            let strategy;
            if (targetMode === 'auto')
                strategy = await applyAutoTemplate(template.id, preferredQuote, config, environment, replace);
            else if (targetMode === 'list')
                strategy = await applyParametricTemplate(template.id, parsedBases, preferredQuote, config, environment, replace);
            else
                strategy = await applyStrategyTemplate(template.id, symbol, config, environment, replace);
            btnClose.current.click();
            if (onApplied) onApplied(strategy);
        } catch (err) {
            // Conflito de nome: o backend devolve { conflict, strategyName }. Oferece
            // substituir (remove a estratégia antiga + componentes) e reaplica.
            const data = err?.response?.data;
            if (err?.response?.status === 409 && data && data.conflict) {
                const ok = await confirmDialog({
                    title: 'Substituir estratégia',
                    message: `Já existe a estratégia "${data.strategyName}". Substituir remove a anterior e todos os seus componentes (automações e order templates) e recria do zero. Continuar?`,
                    confirmLabel: 'Substituir',
                    confirmVariant: 'danger'
                });
                if (ok) { await onApply(true); return; }
                setError(`Operação cancelada. A estratégia "${data.strategyName}" já existe.`);
            } else {
                setError(processError(err));
            }
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className='modal fade' id='modalTemplateConfig' tabIndex='-1' role='dialog' aria-hidden='true'>
            <div className='modal-dialog modal-dialog-centered' role='document'>
                <div className='modal-content'>
                {!template ? null : <>
                    <div className='modal-header'>
                        <div>
                            <p className='modal-title mb-0 fw-bold'>{template.name}</p>
                            <span className={`badge ${DIFFICULTY_BADGE[template.difficulty] || 'bg-secondary'} me-1`}>{template.difficulty}</span>
                            {(template.tags || []).map(t => (
                                <span key={t} className='badge bg-secondary me-1'>{t}</span>
                            ))}
                        </div>
                        <button ref={btnClose} type='button' className='btn-close' data-bs-dismiss='modal' aria-label='Fechar'></button>
                    </div>

                    <div className='modal-body'>
                        <p className='text-muted small mb-3'>{template.description}</p>

                        {canParametric && (
                            <div className='mb-3'>
                                <label className='form-label d-block'>Alvo da estratégia</label>
                                <div className='btn-group w-100' role='group'>
                                    <button type='button'
                                        className={`btn btn-sm ${targetMode === 'single' ? 'btn-primary' : 'btn-outline-primary'}`}
                                        onClick={() => setTargetMode('single')}>
                                        Um ativo
                                    </button>
                                    <button type='button'
                                        className={`btn btn-sm ${targetMode === 'list' ? 'btn-primary' : 'btn-outline-primary'}`}
                                        onClick={() => setTargetMode('list')}>
                                        Vários ativos
                                    </button>
                                    {supportsAuto(template) && (
                                        <button type='button'
                                            className={`btn btn-sm ${targetMode === 'auto' ? 'btn-primary' : 'btn-outline-primary'}`}
                                            onClick={() => setTargetMode('auto')}>
                                            Automático
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                        {targetMode === 'single' && (
                            <div className='mb-3'>
                                <label className='form-label'>Ativo (símbolo)</label>
                                <SelectSymbol symbol={symbol} onChange={onSymbolChange} disabled={false} />
                                <small className='text-muted'>Par de negociação, ex: BTCUSDT.</small>
                            </div>
                        )}

                        {targetMode === 'auto' && isFutures && (
                            <div className='mb-3'>
                                <label className='form-label'>Alvo <span className='badge bg-light text-dark'>× {preferredQuote}</span></label>
                                <div className='alert alert-info py-2 small mb-2'>
                                    🤖 O bot escolhe as moedas {preferredQuote} <strong>em queda</strong> (recomendadas pelo scanner + maior queda nas últimas 24h) e abre um <strong>short</strong> em cada uma. Você não precisa escolher os pares.
                                </div>
                                <small className='text-muted d-block'>
                                    Quantas moedas entram ao mesmo tempo é o campo <strong>Nº de moedas</strong> abaixo. A <strong>margem%</strong> é dividida entre elas, e cada short só abre se aquela moeda estiver em queda confirmada.
                                </small>
                            </div>
                        )}

                        {targetMode === 'auto' && !isFutures && (
                            <div className='mb-3'>
                                <label className='form-label'>Alvo <span className='badge bg-light text-dark'>× {preferredQuote}</span></label>
                                <div className='alert alert-info py-2 small mb-2'>
                                    🤖 O <strong>scanner de mercado</strong> escolhe os melhores ativos {preferredQuote} automaticamente e a estratégia opera neles, trocando conforme o mercado muda. Você não precisa escolher os pares.
                                </div>
                                <small className='text-muted d-block'>
                                    Quantos ativos rodam ao mesmo tempo{template.id === 'EMA_CROSSOVER' || template.id === 'DCA_DIP' ? '' : ' e o consumo de IA'} seguem seu <strong>Perfil de carga</strong> (em Configurações).
                                </small>
                                {template.id === 'AI_TRADER' ? (
                                    <small className='text-muted d-block mt-1'>
                                        🧠 A <strong>IA analisa e opera cada ativo em foco</strong> (compra/venda por sinal). É a versão automática do AI Trader — o scanner cuida da escolha, a IA cuida das entradas e saídas.
                                    </small>
                                ) : template.id === 'EMA_CROSSOVER' || template.id === 'DCA_DIP' ? (
                                    <small className='text-muted d-block mt-1'>
                                        📈 A estratégia aplica suas <strong>regras técnicas</strong> ({template.id === 'EMA_CROSSOVER' ? 'cruzamento de médias' : 'compra na queda por RSI'}) em cada ativo em foco — <strong>sem gastar tokens de IA</strong>. O scanner cuida da escolha; se você não tiver provedor de IA, ele seleciona os ativos por regras (score do mercado).
                                    </small>
                                ) : (
                                    <small className='text-muted d-block mt-1'>
                                        ⚖️ <strong>Alocação híbrida:</strong> os melhores ativos rodam <strong>com IA</strong>; se sobrar capacidade de máquina além do orçamento de tokens, os ativos excedentes rodam num <strong>trilho técnico por regras</strong> (regime + pullback) — sem gastar token. Assim você não fica parado mesmo com IA econômica.
                                    </small>
                                )}
                            </div>
                        )}

                        {targetMode === 'list' && (
                            <div className='mb-3'>
                                <label className='form-label'>Ativos base <span className='badge bg-light text-dark'>× {preferredQuote}</span></label>
                                <input
                                    type='text'
                                    className='form-control'
                                    placeholder='BTC, ETH, SOL'
                                    value={basesText}
                                    onChange={e => setBasesText(e.target.value)}
                                />
                                {parsedBases.length > 0 && (
                                    <div className='mt-2'>
                                        {parsedBases.map(b => (
                                            <span key={b} className='badge bg-secondary me-1 mb-1'>{b}{preferredQuote}</span>
                                        ))}
                                    </div>
                                )}
                                <small className='text-muted d-block mt-1'>
                                    Uma única estratégia opera todos esses ativos. O quote ({preferredQuote}) vem das suas Configurações.
                                </small>
                                {isFutures ? (
                                    <div className='alert alert-warning py-2 small mt-2 mb-0'>
                                        ⚡ Cada moeda abre um <strong>short alavancado</strong> quando entrar em queda confirmada. A <strong>margem%</strong> é dividida entre elas. Comece com poucas (2–3) — mais shorts = mais caminhos de liquidação.
                                    </div>
                                ) : (
                                    <div className='alert alert-warning py-2 small mt-2 mb-0'>
                                        ⚡ Cada ativo é analisado pela IA separadamente — mais ativos consomem mais cota de IA. Comece com poucos (2–3) e acompanhe seu consumo.
                                    </div>
                                )}
                            </div>
                        )}

                        {template.presets && (
                            <div className='mb-3'>
                                <div className='d-flex justify-content-between align-items-center'>
                                    <label className='form-label mb-0'>Nível de risco</label>
                                    {hasAdvancedFields && (
                                        <div className='btn-group btn-group-sm' role='group' aria-label='Modo do formulário'>
                                            <button type='button' className={`btn ${formMode === 'simple' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => changeFormMode('simple')}>Simples</button>
                                            <button type='button' className={`btn ${formMode === 'advanced' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => changeFormMode('advanced')}>Avançado</button>
                                        </div>
                                    )}
                                </div>
                                <div className='btn-group w-100 mt-1' role='group'>
                                    {PRESET_ORDER.map(name => (
                                        <button key={name} type='button'
                                            className={`btn btn-sm ${activePreset === name ? `btn-${PRESET_META[name].cls}` : `btn-outline-${PRESET_META[name].cls}`}`}
                                            onClick={() => applyPreset(name)}>
                                            {PRESET_META[name].label}
                                        </button>
                                    ))}
                                </div>
                                <small className='text-muted d-block mt-1'>
                                    {!activePreset
                                        ? '⚙️ Personalizado — seus valores não batem com nenhum nível. Clique num nível para redefinir.'
                                        : formMode === 'simple' && hasAdvancedFields
                                            ? 'O nível já configurou tudo. Abra o Avançado para ajustar cada parâmetro.'
                                            : 'Pré-preenche os campos abaixo — você ainda pode ajustar cada um.'}
                                </small>
                            </div>
                        )}

                        {testnetUnavailable.length > 0 ? (
                            <div className='alert alert-danger py-2' style={{ fontSize: 13 }}>
                                🛑 <strong>{testnetUnavailable.join(', ')}</strong> {testnetUnavailable.length > 1 ? 'não existem' : 'não existe'} na
                                testnet — neste ambiente {testnetUnavailable.length > 1 ? 'as estratégias ficariam' : 'a estratégia ficaria'} sem
                                dados (nunca dispara). Use um par disponível (ex.: DOGEUSDT) ou crie em produção.
                            </div>
                        ) : lowLiq.length > 0 && (
                            <div className='alert alert-warning py-2' style={{ fontSize: 13 }}>
                                ⚠️ <strong>Baixa liquidez:</strong> {lowLiq.join(', ')} {lowLiq.length > 1 ? 'têm' : 'tem'} volume 24h baixo
                                (&lt; 1M). Moeda ilíquida = fills piores (slippage) e mais difícil de sair. Dá pra criar mesmo assim,
                                mas considere orçamento menor ou outro ativo.
                            </div>
                        )}

                        {fieldsToShow.map(field => (
                            <div className='mb-3' key={field.key}>
                                <label className='form-label'>{field.label}</label>
                                {field.type === 'select' ? (
                                    <select
                                        className='form-select'
                                        value={config[field.key] ?? field.default}
                                        onChange={e => onFieldChange(field.key, e.target.value)}
                                    >
                                        {(field.options || []).map(opt => (
                                            <option key={opt} value={opt}>{opt}</option>
                                        ))}
                                    </select>
                                ) : (
                                    <input
                                        type='number'
                                        className='form-control'
                                        min={field.min}
                                        max={field.max}
                                        value={config[field.key] ?? field.default}
                                        onChange={e => onFieldChange(field.key, e.target.value)}
                                    />
                                )}
                                {field.hint && <small className='text-muted'>{field.hint}</small>}
                            </div>
                        ))}

                        <div className={`alert py-2 small mt-2 ${isTest ? 'alert-warning' : 'alert-info'}`}>
                            {isTest
                                ? <>Automações criadas em <strong>ambiente de teste</strong> 🧪 — visíveis na aba Teste de Automações. Promova para produção quando validar.</>
                                : <>Automações criadas em <strong>produção</strong>. Para testar antes, troque para o ambiente Teste antes de usar o template.</>
                            }
                            {' '}A estratégia nasce <strong>inativa</strong>.
                        </div>
                    </div>

                    <div className='modal-footer'>
                        <ActionMessage error={error} extraClassName='mt-1 col-8 py-1' />
                        <button type='button' className='btn btn-sm btn-primary' onClick={() => onApply()} disabled={loading}>
                            {loading ? 'Criando...' : 'Criar Estratégia'}
                        </button>
                    </div>
                </>}
                </div>
            </div>
        </div>
    );
}

export default TemplateConfigModal;
